import styled from "@emotion/styled";
import React from "react";
import useOpen from "../../../hooks/useOpen";
import { DutySheetTask, PlanOfCareItemId, VisitInstanceId } from "../../../messages";
import { CHECK_SVG, TIMES_SVG } from "../../../utils/svgs";
import { AppButton } from "../../Generic/AppButton";
import { AppCardHeader } from "../../Generic/AppCard";
import { AppIcon } from "../../Generic/AppIcon";
import Flex from "../../Generic/Flex";
import { If } from "../../Generic/If";
import Textarea from "../../Generic/Textarea/Textarea";
import { default as NotesVector } from "/public/assets/notes.svg?react";
import { AnimatePresence, motion } from "framer-motion";
import { translate } from "../../../translate";
import { STRINGS } from "../../../strings";
import { isLoading } from "../../../Loading";
import useSessionStorage from "../../../hooks/useSessionStorage";
import { getDutySheetVisitTaskKey } from "../../../clockInOutUtils";

interface DutyTaskItemProps {
  activeVisitId: VisitInstanceId;
  duty: DutySheetTask;
  notes: string | null;
  isChecked: boolean;
  isApproved: boolean | null;
  colored: boolean;
  isTaskItemDisabled?: boolean;
  isRequired?: boolean;
  onApproveDuty: (duty: PlanOfCareItemId, approval: boolean) => void;
  onChangeNotes: (id: PlanOfCareItemId, notes: string) => void;
  onCheck: (id: PlanOfCareItemId, isChecked: boolean) => void;
  onDisabledTaskItemClick?: () => void;
}

function DutyTaskItem(props: DutyTaskItemProps) {
  const { isOpen: isNotesOpen, actions } = useOpen({ initialState: props.notes !== null });
  const [duty, setDutyApprovalStatus] = useSessionStorage(
    getDutySheetVisitTaskKey(props.duty.id, props.activeVisitId),
    props.isApproved
  );

  const handleClick = (approved: boolean) => {
    setDutyApprovalStatus(!isLoading(duty) && duty !== approved ? approved : null);
    props.onApproveDuty(props.duty.id, approved);
    if (isNotesOpen) {
      actions.toggle();
    }
  };

  const handleChangeNotes = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    props.onChangeNotes(props.duty.id, e.target.value);
  };

  const notesButton = (
    <If condition={props.isTaskItemDisabled !== true}>
      <If condition={!isNotesOpen && !isLoading(duty) && duty !== false}>
        <DutyTaskItem.Button
          color="black"
          variant="link"
          disabled={props.isTaskItemDisabled}
          onClick={actions.toggle}
        >
          <NotesVector />
        </DutyTaskItem.Button>
      </If>
      <If condition={isNotesOpen && !isLoading(duty) && duty !== false}>
        <DutyTaskItem.Button
          color="primary"
          variant="outline"
          disabled={props.isTaskItemDisabled}
          onClick={actions.toggle}
        >
          <NotesVector />
        </DutyTaskItem.Button>
      </If>
      <If condition={props.isApproved === false}>
        <DutyTaskItem.Button color="primary" variant="outline" disabled={true}>
          <NotesVector />
        </DutyTaskItem.Button>
      </If>
    </If>
  );

  return (
    <DutyTaskItem.CardHeader id={getDutySheetVisitTaskKey(props.duty.id, props.activeVisitId)}>
      <DutyTaskItem.ItemWrapper>
        <Flex direction="row" alignItems="flex-start" justifyContent="flex-start">
          <DutyTaskItem.Span colored={props.colored}>
            {props.duty.name}
            <If condition={props.isRequired === true}>
              <DutyTaskItem.Badge>{"*"}</DutyTaskItem.Badge>
            </If>
          </DutyTaskItem.Span>
        </Flex>

        <Flex direction="row" xGap={4} justifyContent="flex-end" alignItems="center">
          <If condition={!isLoading(duty) && duty !== null && props.notes === null}>
            {notesButton}
          </If>

          <If condition={!isLoading(duty) && duty !== false && !props.colored}>
            <DutyTaskItem.Button
              variant="outline"
              color="black"
              disabled={props.isTaskItemDisabled}
              onClick={() => handleClick(false)}
            >
              <AppIcon icon={TIMES_SVG} size={10}></AppIcon>
            </DutyTaskItem.Button>
          </If>
          <If condition={!isLoading(duty) && duty !== false && props.colored}>
            <DutyTaskItem.Button
              variant="outline"
              color="danger"
              disabled={props.isTaskItemDisabled}
              onClick={() => handleClick(false)}
            >
              <AppIcon icon={TIMES_SVG} size={10}></AppIcon>
            </DutyTaskItem.Button>
          </If>
          <If condition={!isLoading(duty) && duty === false}>
            <DutyTaskItem.CloseButton color="danger" disabled={props.isTaskItemDisabled}>
              <AppIcon icon={TIMES_SVG} size={10} onClick={() => handleClick(false)}></AppIcon>
            </DutyTaskItem.CloseButton>
          </If>
          <If condition={!isLoading(duty) && duty === true}>
            <DutyTaskItem.ApproveButton
              color="success-dark"
              disabled={props.isTaskItemDisabled}
              onClick={() => handleClick(true)}
            >
              <AppIcon icon={CHECK_SVG} size={10}></AppIcon>
            </DutyTaskItem.ApproveButton>
          </If>
          <If condition={!isLoading(duty) && duty !== true && !props.colored}>
            <DutyTaskItem.Button
              variant="outline"
              color="black"
              disabled={props.isTaskItemDisabled}
              onClick={() => handleClick(true)}
            >
              <AppIcon icon={CHECK_SVG} size={10}></AppIcon>
            </DutyTaskItem.Button>
          </If>
          <If condition={!isLoading(duty) && duty !== true && props.colored}>
            <DutyTaskItem.Button
              variant="outline"
              color="danger"
              disabled={props.isTaskItemDisabled}
              onClick={() => handleClick(true)}
            >
              <AppIcon icon={CHECK_SVG} size={10}></AppIcon>
            </DutyTaskItem.Button>
          </If>
        </Flex>
      </DutyTaskItem.ItemWrapper>
      <AnimatePresence>
        {((!isLoading(duty) && duty === false) || isNotesOpen) && (
          <motion.div
            key={`${props.duty.id}`}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ type: "spring", stiffness: 500, damping: 50, duration: 0.5 }}
          >
            <DutyTaskItem.Textarea
              value={props.notes ?? ""}
              disabled={props.isTaskItemDisabled}
              placeholder={
                !isLoading(duty) && duty === true
                  ? translate(STRINGS.ADDITIONAL_NOTES)
                  : translate(STRINGS.PLEASE_EXPLAIN_WHY_IT_WAS_NOT_COMPLETED)
              }
              onChange={handleChangeNotes}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </DutyTaskItem.CardHeader>
  );
}

DutyTaskItem.Root = styled.div`
  & > .title {
    padding: 12px 0;
  }

  min-height: 56px;
`;

DutyTaskItem.Span = styled.span<{ colored: boolean }>`
  color: ${(props) => (props.colored ? `var(--danger-color);` : `black`)};
`;

DutyTaskItem.Badge = styled.span`
  font-size: 14px;
  color: var(--danger-color);
`;

DutyTaskItem.CloseButton = styled(AppButton)`
  width: 36px;
  height: 36px;

  border-radius: 8px;
  padding: 0;
`;

DutyTaskItem.ApproveButton = styled(AppButton)`
  width: 36px;
  height: 36px;

  border-radius: 8px;
  padding: 0;
`;

DutyTaskItem.Textarea = styled(Textarea)`
  margin-top: 8px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  color: var(--tw-slate-600);
  font-weight: 400;
`;

DutyTaskItem.Button = styled(AppButton)`
  width: 36px;
  height: 36px;

  border-radius: 8px;
  padding: 0;
`;

DutyTaskItem.CardHeader = styled(AppCardHeader)`
  display: inherit;
  > * + * {
    margin-inline: 12px;
  }
`;

DutyTaskItem.ItemWrapper = styled(Flex)`
  padding: 0 16px 0 16px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

export default DutyTaskItem;
